import { useState, useEffect } from "react";
import { useTranslation } from "../contexts/TranslationContext";
import { fetchDonationsContent } from "../APIClient/apiClient";
import { fetchAPI } from "../APIClient/modules/baseApi";
import { resolveImageSrc } from "../utils/resolveImageSrc";

export function useDonationsContent() {
  const { currentLanguage } = useTranslation();
  const [content, setContent] = useState({
    title: "",
    description: "",
    cardsHtml: "",
    metaTitle: "",
    projects: [],
    headerTexts: [],
  });

  useEffect(() => {
    let isActive = true;

    setContent({
      title: "",
      description: "",
      cardsHtml: "",
      metaTitle: "",
      projects: [],
      headerTexts: [],
    });

    async function loadContent() {
      try {
        // Fetch page content (meta_title, title, description)
        const pageResponse = await fetchAPI("/donations/page", { method: "GET" });
        const pageData = Array.isArray(pageResponse) ? pageResponse : [];

        const enPageData = pageData.find((row) => row.language === "en") || {};
        const viPageData = pageData.find((row) => row.language === "vi") || {};
        const currentPageData = currentLanguage === "en" ? enPageData : viPageData;

        // Fetch dynamic projects data
        const donationsData = await fetchDonationsContent(currentLanguage);

        // Get static cardsHtml
        const staticCards = getStaticDonationsCardsHtml(currentLanguage);

        if (!isActive) return;

        setContent({
          metaTitle: currentPageData.meta_title || "",
          title: currentPageData.title || "",
          description: currentPageData.description || "",
          cardsHtml: staticCards,
          projects: donationsData?.projects || [],
          headerTexts: donationsData?.headerTexts || [],
        });
      } catch (error) {
        console.error("Failed to fetch donations content:", error);
        if (!isActive) return;

        // Fallback to static content
        const staticContent = getStaticDonationsContent(currentLanguage);
        setContent({
          title: staticContent.title,
          description: staticContent.description,
          cardsHtml: staticContent.cardsHtml,
          metaTitle: staticContent.metaTitle,
          projects: [],
          headerTexts: [],
        });
      }
    }

    loadContent();

    return () => {
      isActive = false;
    };
  }, [currentLanguage]);

  const getStaticDonationsCardsHtml = (language) => {
    const donationQrSrc = resolveImageSrc("images/donations/donation_account.png");

    if (language === "en") {
      return `
        <div class="contact-card">
          <h2 class="contact-title">Vietnam</h2>
          <div class="contact-row">
            <p class="contact-text">1. If you would like to support VietQubit in Vietnam, please donate by scanning the QR code below:</p>
          </div>
          <div class="contact-row qr-row">
            <div class="contact-text qr-wrapper">
              <img src="${donationQrSrc}" alt="Donation QR code for VietQubit in Vietnam" class="donation-qr">
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. Please include the following in the transfer description: <strong>[Full name / Organization] donation to VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. <strong>Note:</strong> Your name or organization will only be made public upon request.</p>
          </div>
        </div>
        <div class="contact-card">
          <h2 class="contact-title">International</h2>
          <div class="contact-row">
            <div class="contact-text">
              <p>1. If you wish to support VietQubit from outside Vietnam, you can make a bank transfer:</p>
              <ul class="contact-text">
                <li>Bank name: Shinhan Bank Vietnam</li>
                <li>Account number: XXXX XXXX XXXX XXXX</li>
                <li>Account holder: XXXX XXXX XXXX XXXX</li>
                <li>SWIFT code: XXXX XXXX XXXX XXXX</li>
              </ul>
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. You can also support VietQubit via PayPal transfer: XXXX XXXX XXXX XXXX</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. Please include the following in the transfer description: <strong>[Full name / Organization] donation to VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">4. <strong>Note:</strong> Your name or organization will only be made public upon request.</p>
          </div>
        </div>
      `;
    }

    return `
        <div class="contact-card">
          <h2 class="contact-title">Việt Nam</h2>
          <div class="contact-row">
            <p class="contact-text">1. Nếu bạn muốn tài trợ cho VietQubit tại Việt Nam, vui lòng tài trợ thông qua quét mã QR bên dưới:</p>
          </div>
          <div class="contact-row qr-row">
            <div class="contact-text qr-wrapper">
              <img src="${donationQrSrc}" alt="QR tài trợ VietQubit tại Việt Nam" class="donation-qr">
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. Nội dung chuyển khoản ghi rõ: <strong>[Họ và tên/ Đơn vị] tài trợ cho nhóm VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. <strong>Lưu ý:</strong> Chúng tôi chỉ công bố danh tính hoặc tổ chức của bạn khi có yêu cầu.</p>
          </div>
        </div>
        <div class="contact-card">
          <h2 class="contact-title">Quốc tế</h2>
          <div class="contact-row">
            <div class="contact-text">
              <p>1. Nếu bạn muốn tài trợ cho VietQubit ngoài Việt Nam, bạn có thể chuyển khoản ngân hàng:</p>
              <ul class="contact-text">
                <li>Tên ngân hàng: Ngân hàng Shinhan Việt Nam</li>
                <li>Số tài khoản: XXXX XXXX XXXX XXXX</li>
                <li>Tên người nhận: XXXX XXXX XXXX XXXX</li>
                <li>Mã SWIFT: XXXX XXXX XXXX XXXX</li>
              </ul>
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. Ngoài ra, bạn cũng có thể tài trợ cho VietQubit qua chuyển khoản PayPal: XXXX XXXX XXXX XXXX</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. Nội dung chuyển khoản ghi rõ: <strong>[Họ và tên/ Đơn vị] tài trợ cho nhóm VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">4. <strong>Lưu ý:</strong> Chúng tôi chỉ công bố danh tính hoặc tổ chức của bạn khi có yêu cầu.</p>
          </div>
        </div>
      `;
  };

  const getStaticDonationsContent = (language) => {
    const donationQrSrc = resolveImageSrc("images/donations/donation_account.png");
    const viContent = {
      metaTitle: "Tài trợ | VietQubit",
      title: "Tài trợ",
      description:
        "VietQubit là một nhóm nghiên cứu độc lập, không trực thuộc hay chịu sự quản lý của bất kỳ tổ chức hay doanh nghiệp nào. Hoạt động của chúng tôi dựa trên nguồn kinh phí tự có, cùng sự đóng góp và tài trợ quý báu từ cộng đồng và các đối tác. Chúng tôi theo đuổi sứ mệnh thúc đẩy sự phát triển nhanh chóng của các công nghệ trí tuệ nhân tạo (AI) mang lại tác động tích cực và có ý nghĩa cho thế giới. Mỗi khoản quyên góp của bạn đều góp phần trực tiếp vào việc xây dựng một hệ sinh thái nghiên cứu cởi mở, nơi tri thức được chia sẻ và đổi mới được khuyến khích. Nguồn tài trợ nhận được sẽ được sử dụng minh bạch và nghiêm túc cho các hoạt động của VietQubit, bao gồm: 10% dành cho thuế VAT, 40% cho chi phí vận hành (tên miền, hạ tầng máy chủ, thù lao cộng tác viên), 20% trích lập quỹ dự phòng phát triển dự án và 30% hỗ trợ các dự án nghiên cứu, sản xuất nội dung (podcast, hội thảo khoa học) và tài liệu hướng dẫn. Mỗi sự đóng góp, dù nhỏ hay lớn, đều là nguồn động lực to lớn giúp chúng tôi tiếp tục hành trình nghiên cứu, sáng tạo và chia sẻ tri thức mở về AI. Xin chân thành cảm ơn sự đồng hành của bạn!",
      cardsHtml: `
        <div class="contact-card">
          <h2 class="contact-title">Việt Nam</h2>
          <div class="contact-row">
            <p class="contact-text">1. Nếu bạn muốn tài trợ cho VietQubit tại Việt Nam, vui lòng tài trợ thông qua quét mã QR bên dưới:</p>
          </div>
          <div class="contact-row qr-row">
            <div class="contact-text qr-wrapper">
              <img src="${donationQrSrc}" alt="QR tài trợ VietQubit tại Việt Nam" class="donation-qr">
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. Nội dung chuyển khoản ghi rõ: <strong>[Họ và tên/ Đơn vị] tài trợ cho nhóm VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. <strong>Lưu ý:</strong> Chúng tôi chỉ công bố danh tính hoặc tổ chức của bạn khi có yêu cầu.</p>
          </div>
        </div>
        <div class="contact-card">
          <h2 class="contact-title">Quốc tế</h2>
          <div class="contact-row">
            <div class="contact-text">
              <p>1. Nếu bạn muốn tài trợ cho VietQubit ngoài Việt Nam, bạn có thể chuyển khoản ngân hàng:</p>
              <ul class="contact-text">
                <li>Tên ngân hàng: Ngân hàng Shinhan Việt Nam</li>
                <li>Số tài khoản: XXXX XXXX XXXX XXXX</li>
                <li>Tên người nhận: XXXX XXXX XXXX XXXX</li>
                <li>Mã SWIFT: XXXX XXXX XXXX XXXX</li>
              </ul>
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. Ngoài ra, bạn cũng có thể tài trợ cho VietQubit qua chuyển khoản PayPal: XXXX XXXX XXXX XXXX</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. Nội dung chuyển khoản ghi rõ: <strong>[Họ và tên/ Đơn vị] tài trợ cho nhóm VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">4. <strong>Lưu ý:</strong> Chúng tôi chỉ công bố danh tính hoặc tổ chức của bạn khi có yêu cầu.</p>
          </div>
        </div>
      `,
    };

    const enContent = {
      metaTitle: "Donations | VietQubit",
      title: "Donations",
      description:
        "VietQubit is an independent research group, not affiliated with or managed by any organization or company. Our activities are funded by our own resources together with generous contributions and sponsorships from the community and partners. We pursue the mission of accelerating the development of artificial intelligence (AI) technologies that create positive and meaningful impact on the world. Every contribution you make helps build an open research ecosystem where knowledge is shared and innovation is encouraged. All donations are used transparently and responsibly for VietQubit's activities, including: 10% for VAT, 40% for operating costs (domains, server infrastructure, collaborators), 20% for contingency and project reserve, and 30% for research projects, educational materials, and content production (podcasts, scientific workshops). Any contribution, whether small or large, is a strong motivation for us to continue our journey of open AI research and knowledge sharing. Thank you for your support!",
      cardsHtml: `
        <div class="contact-card">
          <h2 class="contact-title">Vietnam</h2>
          <div class="contact-row">
            <p class="contact-text">1. If you would like to support VietQubit in Vietnam, please donate by scanning the QR code below:</p>
          </div>
          <div class="contact-row qr-row">
            <div class="contact-text qr-wrapper">
              <img src="${donationQrSrc}" alt="Donation QR code for VietQubit in Vietnam" class="donation-qr">
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. Please include the following in the transfer description: <strong>[Full name / Organization] donation to VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. <strong>Note:</strong> Your name or organization will only be made public upon request.</p>
          </div>
        </div>
        <div class="contact-card">
          <h2 class="contact-title">International</h2>
          <div class="contact-row">
            <div class="contact-text">
              <p>1. If you wish to support VietQubit from outside Vietnam, you can make a bank transfer:</p>
              <ul class="contact-text">
                <li>Bank name: Shinhan Bank Vietnam</li>
                <li>Account number: XXXX XXXX XXXX XXXX</li>
                <li>Account holder: XXXX XXXX XXXX XXXX</li>
                <li>SWIFT code: XXXX XXXX XXXX XXXX</li>
              </ul>
            </div>
          </div>
          <div class="contact-row">
            <p class="contact-text">2. You can also support VietQubit via PayPal transfer: XXXX XXXX XXXX XXXX</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">3. Please include the following in the transfer description: <strong>[Full name / Organization] donation to VietQubit</strong>.</p>
          </div>
          <div class="contact-row">
            <p class="contact-text">4. <strong>Note:</strong> Your name or organization will only be made public upon request.</p>
          </div>
        </div>
      `,
    };

    return language === "en" ? enContent : viContent;
  };

  return content;
}
